import { Injectable } from '@angular/core';
import { LOCAL_STORAGE_KEY, SESSION_STORAGE_KEY } from '@core/configs/local-storage-key.config';
import { Mode } from '@core/constants';
import { LocalStorageService } from './local-storage.service';
import { SessionContextService } from './session-context.service';
import { SessionStorageService } from './session-storage.service';

export interface TvLoginSession {
  corp_prefix: string;
  en_corp_prefix?: string;
  login_session: any;
  officeSelected: any;
  staff_inf: any;
}

@Injectable({
  providedIn: 'root'
})
export class TvLoginSessionService {
  constructor(
    private readonly localStorageService: LocalStorageService,
    private readonly sessionStorageService: SessionStorageService,
    private readonly sessionContextService: SessionContextService
  ) { }

  persistCurrentSession(): TvLoginSession | null {
    const mode = this.localStorageService.getItem(LOCAL_STORAGE_KEY.mode) || Mode.WEB;
    if (mode !== Mode.TV) {
      return null;
    }

    const session = this.buildCurrentSession();
    if (!this.isRestorableSession(session)) {
      return null;
    }

    this.localStorageService.setItem(LOCAL_STORAGE_KEY.tvLoginSession, session);
    this.log('ログイン情報を保存しました');
    return session;
  }

  restoreSession(): boolean {
    if (this.sessionStorageService.getUserSession()) {
      // session restored by native app (keep login) has no login time yet
      if (this.isTvMode() && !this.sessionStorageService.getSessionData(SESSION_STORAGE_KEY.loginDateTime)) this.sessionStorageService.setLoginDateTime();
      return false;
    }

    const session = this.localStorageService.getItem<TvLoginSession>(LOCAL_STORAGE_KEY.tvLoginSession);
    if (!this.isRestorableSession(session)) {
      this.clear();
      return false;
    }

    this.localStorageService.setItem(LOCAL_STORAGE_KEY.mode, Mode.TV);
    this.localStorageService.setItem(LOCAL_STORAGE_KEY.corpPrefix, session.corp_prefix);
    this.localStorageService.setItem(LOCAL_STORAGE_KEY.enCorpPrefix, session.en_corp_prefix || session.corp_prefix);
    this.sessionStorageService.setSessionData(SESSION_STORAGE_KEY.loginSession, session.login_session);
    this.sessionStorageService.setSessionData(SESSION_STORAGE_KEY.staffInfo, session.staff_inf);
    this.sessionContextService.setSelectedOfficeIdentity(session.officeSelected);
    this.sessionStorageService.setLoginDateTime();
    this.log('保存済みログイン情報を復元しました');
    return true;
  }

  private isTvMode(): boolean {
    return this.localStorageService.getItem(LOCAL_STORAGE_KEY.mode) === Mode.TV;
  }

  clear(): void {
    this.localStorageService.removeItem(LOCAL_STORAGE_KEY.tvLoginSession);
  }

  private buildCurrentSession(): TvLoginSession {
    return {
      corp_prefix: this.localStorageService.getItem(LOCAL_STORAGE_KEY.corpPrefix) || '',
      en_corp_prefix: this.localStorageService.getItem(LOCAL_STORAGE_KEY.enCorpPrefix) || '',
      login_session: this.sessionStorageService.getSessionData(SESSION_STORAGE_KEY.loginSession),
      officeSelected: this.sessionContextService.getOfficeSelected(),
      staff_inf: this.sessionContextService.getStaffInfo(),
    };
  }

  private isRestorableSession(session: TvLoginSession | null): session is TvLoginSession {
    return !!session?.corp_prefix
      && !!session?.login_session?.refreshToken
      && !!session?.officeSelected
      && !!session?.staff_inf;
  }

  private log(message: string): void {
    console.info(`[TV Startup] ${message}`);
  }
}
